/**
 * Phase 61 Plan 06 — Weight setting editor component.
 *
 * React Hook Form with 6 integer fields (kudosBasePoints + 5 behaviour sources).
 * "Preview" button runs previewWeightImpact without saving.
 * "Save and trigger recompute" button calls updateWeights and triggers band rebuild.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRespectWeights, useUpdateWeights, usePreviewWeightImpact } from '../../../hooks/useRespectAdmin';
import type { PreviewResultDTO } from '../../../services/api/respectAdmin';
import { RecomputePreviewChart } from './RecomputePreviewChart';

// ── Field config ──────────────────────────────────────────────────────────────

const WEIGHT_FIELDS = [
  { key: 'kudosBasePoints', label: 'Kudos base points (per kudos × tag weight)' },
  { key: 'onTimeTimesheetPoints', label: 'On-time timesheet (per week)' },
  { key: 'slaResponsivenessPoints', label: 'SLA task responsiveness (per task)' },
  { key: 'engineeringCommitActivityPoints', label: 'Engineering commit activity (per commit)' },
  { key: 'projectClosureQualityPoints', label: 'Project closure quality (per project)' },
  { key: 'teamEventAttendancePoints', label: 'Team event attendance (per event)' },
] as const;

type WeightFormValues = {
  kudosBasePoints: number;
  onTimeTimesheetPoints: number;
  slaResponsivenessPoints: number;
  engineeringCommitActivityPoints: number;
  projectClosureQualityPoints: number;
  teamEventAttendancePoints: number;
};

// ── Component ─────────────────────────────────────────────────────────────────

export function WeightSettingEditor() {
  const { data: weights, isLoading } = useRespectWeights();
  const updateWeights = useUpdateWeights();
  const previewMutation = usePreviewWeightImpact();
  const [previewResult, setPreviewResult] = useState<PreviewResultDTO | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<WeightFormValues>({
    defaultValues: {
      kudosBasePoints: 10,
      onTimeTimesheetPoints: 5,
      slaResponsivenessPoints: 5,
      engineeringCommitActivityPoints: 5,
      projectClosureQualityPoints: 5,
      teamEventAttendancePoints: 5,
    },
  });

  // Sync form with loaded weights
  useEffect(() => {
    if (weights) {
      reset({
        kudosBasePoints: weights.kudosBasePoints,
        onTimeTimesheetPoints: weights.onTimeTimesheetPoints,
        slaResponsivenessPoints: weights.slaResponsivenessPoints,
        engineeringCommitActivityPoints: weights.engineeringCommitActivityPoints,
        projectClosureQualityPoints: weights.projectClosureQualityPoints,
        teamEventAttendancePoints: weights.teamEventAttendancePoints,
      });
    }
  }, [weights, reset]);

  function handlePreview() {
    const values = getValues();
    previewMutation.mutate(values, {
      onSuccess: (result) => setPreviewResult(result),
    });
  }

  function onSubmit(values: WeightFormValues) {
    setPreviewResult(null);
    updateWeights.mutate(values, {
      onSuccess: () => {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        reset(values); // mark form as pristine
      },
    });
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading weights…</p>;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {WEIGHT_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
              type="number"
              min={0}
              step={1}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
              {...register(key, {
                valueAsNumber: true,
                min: { value: 0, message: 'Must be ≥ 0' },
              })}
            />
            {errors[key] && (
              <p className="text-xs text-red-600 mt-0.5">{errors[key]?.message}</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewMutation.isPending}
          className="border border-gray-300 text-gray-700 px-4 py-1.5 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {previewMutation.isPending ? 'Computing…' : 'Preview'}
        </button>
        <button
          type="submit"
          disabled={updateWeights.isPending || !isDirty}
          className="bg-primary-600 text-white px-4 py-1.5 rounded text-sm hover:bg-primary-700 disabled:opacity-50"
        >
          {updateWeights.isPending ? 'Saving…' : 'Save and trigger recompute'}
        </button>
        {saveSuccess && (
          <span className="text-sm text-green-600">Saved. Band recompute queued.</span>
        )}
        {updateWeights.isError && (
          <span className="text-sm text-red-600">Save failed. Please retry.</span>
        )}
      </div>

      {previewResult && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Band distribution preview</h3>
          <RecomputePreviewChart preview={previewResult} />
        </div>
      )}
    </form>
  );
}
